/** React binding for the live connection: the latest snapshot, connection state, a send
 * function, and the last error or ack. The transport (the socket by default, long polling
 * where the network refuses sockets) is chosen in ./transport and invisible from here. */
import { useEffect, useRef, useState } from "react";

import { accessToken, isLocalToken, refreshSession } from "../auth/session";
import type { ConnState, ServerMessage } from "./socket";
import { LiveConnection } from "./transport";

/** One finished round of this session, newest first, from the staff snapshot (backend change
 * B1). The Asked deck reads this, never React state: a reload, a host handoff or a second
 * controller must all show the same lesson history. */
export type AskedEntry = {
  item_id: string;
  attempt: number;
  /** Why this round exists: the first ask, a re-vote after discussion, or a fresh re-ask. */
  basis: "first" | "revote" | "fresh";
  stem: string;
  lo_id: string | null;
  type: string;
  spoken: boolean;
  option_keys: string[];
  opened_at: string;
  closed_at: string | null;
  revealed: string | null;
  answered: number;
  scorable: number;
  correct_ratio: number | null;
  before_correct_ratio: number | null;
  revote_delta: number | null;
  /** The teacher said afterwards which option was right (an ask-aloud round). */
  keyed: boolean;
  correct_keys: string[] | null;
  /** STORY-121 (FR-049): students whose answer landed after the round closed, inside their
   * own channel's window — counted per student, never per answer. */
  late: number;
  /** Students on a channel that could not carry this question to them (unsupported type,
   * or a send that failed): a delivery fact, so it moves when the gateway reports. */
  unreachable: number;
  /** Students still inside their own window on this round, judged at the frame's clock. */
  outstanding?: number;
};

/** What a channel's gateway last reported about itself (`channels.state`). */
export type ChannelState = "ok" | "unreachable" | "disabled";

/** The teacher's channel numbers, on the staff frame only (STORY-121). Every value is a
 * count: the projector and a student's frame carry no `channels` key at all (FR-051). */
export type Channels = {
  /** Per channel, how many students are still inside their own answer window, any round. */
  outstanding: Record<string, number>;
  state: Record<string, ChannelState>;
  /** Per channel, how many of the course's students have ever joined by it. */
  course: Record<string, number>;
  /** For the open round only: how many on each channel cannot be reached with this item. */
  unsupported: Record<string, number>;
};

/** Item types a listed channel can carry — a mirror of `CHANNEL_ITEM_TYPES` in
 * `packages/events/src/events/channels.py`, which is the one place a type is added to a
 * channel. This copy exists so the deck can mark a card *before* it is opened (the frame's
 * `channels.unsupported` covers only the open round); keep the two tables identical. A channel
 * absent from the table carries everything. */
export const CHANNEL_ITEM_TYPES: Readonly<Record<string, ReadonlySet<string>>> = {
  telegram: new Set(["single", "multi"]),
  card: new Set(["single"]),
  clicker: new Set(["single"]),
};

/** The most options a listed channel carries, and it needs at least one — a mirror of
 * `CHANNEL_MAX_OPTIONS` in the same Python file: a printed card is held by one of four edges,
 * a clicker has two buttons (`CLICKER_BUTTONS`). Keep the two tables identical. */
export const CHANNEL_MAX_OPTIONS: Readonly<Record<string, number>> = {
  card: 4,
  clicker: 2,
};

/** The channels that never carry an option image (the pixels would go to a third party) — a
 * mirror of `IMAGE_FREE_CHANNELS` in the same Python file. */
export const IMAGE_FREE_CHANNELS: ReadonlySet<string> = new Set(["telegram"]);

export type Snapshot = {
  state: string;
  host_device_id: string;
  present: number;
  /** Students currently away from the app (STORY-081); always aggregate. */
  away?: number;
  /** Course policy the student is allowed to know: `signups` is STORY-113. */
  policy?: { attention_flags: boolean; signups?: string };
  /** Staff only, and only when the course setting is on. */
  away_respondents?: string[];
  item: {
    item_id: string;
    attempt: number;
    basis?: "first" | "revote" | "fresh";
    deadline: string | null;
    closed: boolean;
    revealed: string | null;
    snapshot: {
      item_id: string;
      lo_id?: string | null;
      type: string;
      stem: string;
      options: { key: string; label?: string; image?: string | null; correct?: boolean; misconception?: string | null }[];
      confidence_enabled?: boolean;
      spoken?: boolean;
      answer_spec?: Record<string, unknown>;
    };
  } | null;
  aggregate: {
    answered: number;
    present: number;
    basis?: "first" | "revote" | "fresh";
    /** The correct answer is one the teacher gave after the close (STORY-119). */
    keyed?: boolean;
    /** Staff only: which options the teacher marked correct afterwards. */
    correct_keys?: string[] | null;
    options: { key: string; label: string; count: number; correct: boolean; misconception: string | null }[];
    correct_count: number;
    scorable: number;
    correct_ratio: number | null;
    confidence: Record<string, number>;
    before: Record<string, number>;
    before_answered?: number;
    before_correct_ratio?: number | null;
    revote_delta?: number | null;
    /** Staff only (STORY-086): students whose latest word is a clicker's "I'm lost" chord. */
    lost?: number;
    /** `lost` is set on a clicker's chord: no answer, and no confidence. */
    per_respondent?: Record<string, { answer: unknown; correct: boolean | null; confidence?: number | null; channel: string; lost?: boolean }>;
    clusters?: { cluster_id: string; label: string; count: number; members?: string[]; suggested?: boolean | null; verdict?: boolean | null }[];
  } | null;
  /** The caller's OWN counted answer for the open (item_id, attempt); never another student's.
   *  `correct` is null until the teacher reveals the answer, and for items the server cannot
   *  score, so the screen has a third state and can never fall through to "wrong". */
  my_response?: { item_id: string; attempt: number; answer: unknown; confidence: number | null; channel: string; correct: boolean | null } | null;
  /** Server clock at render, for the countdown (the device clock is not trusted). */
  server_now?: string;
  suggestions?: { kind: string; reason: string; misconception: string | null; option_key: string | null; recommended: boolean }[];
  respondents?: string[];
  labels?: Record<string, string>;
  /** Staff only: every finished round of this session, newest first. */
  asked?: AskedEntry[];
  /** Staff only: the channel line's numbers (STORY-121). Absent on every other frame. */
  channels?: Channels;
  /** The teacher has released this session's results, so a verdict may no longer change. */
  released?: boolean;
};

export function useLive(
  sessionId: string,
  token: string,
  deviceId: string,
  fallback?: (payload: Record<string, unknown>) => Promise<ServerMessage | null>,
) {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [conn, setConn] = useState<ConnState>("reconnecting");
  const [last, setLast] = useState<ServerMessage | null>(null);
  const [skewMs, setSkewMs] = useState(0);
  const sock = useRef<LiveConnection | null>(null);

  useEffect(() => {
    // a respondent or projector token never expires; a teacher's access token does, and a
    // long-polling connection re-presents it on every request (a socket only at connect)
    const staff = !token.startsWith("rt.") && !token.startsWith("pt.") && !isLocalToken(token);
    const s = new LiveConnection({
      sessionId,
      token,
      deviceId,
      onState: setConn,
      fallback,
      refreshToken: staff ? async () => ((await refreshSession()) ? accessToken() : null) : undefined,
      onMessage: (m) => {
        if (m.kind === "state") {
          const sn = m.data as unknown as Snapshot;
          if (sn.server_now) setSkewMs(Date.parse(sn.server_now) - Date.now());
          setSnapshot(sn);
        } else setLast(m);
      },
    });
    sock.current = s;
    s.connect();
    return () => s.close();
  }, [sessionId, token, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = (payload: Record<string, unknown>) => sock.current?.send(payload);
  return { snapshot, conn, last, send, skewMs };
}
